/**
 * Authentication state.
 *
 * The role carried here is only used to decide what to render. Every protected
 * action is re-authorised by the API, so a tampered client state grants nothing.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  ApiError,
  api,
  getAccessToken,
  onSessionLost,
  renewSession,
  setAccessToken,
} from '@/services/api';
import type { SessionUser, TokenResponse, UserPublic, UserRole } from '@/types/api';

interface AuthState {
  user: UserPublic | null;
  permissions: string[];
  status: 'loading' | 'authenticated' | 'anonymous';
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (...roles: UserRole[]) => boolean;
  can: (permission: string) => boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserPublic | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const queryClient = useQueryClient();

  const clear = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setPermissions([]);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  const loadSession = useCallback(async () => {
    try {
      const session = await api.get<SessionUser>('/auth/me');
      setUser(session.user);
      setPermissions(session.permissions);
      setStatus('authenticated');
    } catch {
      setUser(null);
      setPermissions([]);
      setStatus('anonymous');
    }
  }, []);

  // On mount, restore the session from the refresh cookie so that a page reload
  // does not log the user out. `renewSession` shares a single in-flight request,
  // so a double-mounted provider never sends two renewals with the same cookie.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (getAccessToken()) {
        // Another mount already restored the session.
        if (!cancelled) await loadSession();
        return;
      }
      const renewed = await renewSession();
      if (cancelled) return;
      if (renewed) {
        await loadSession();
      } else {
        setStatus('anonymous');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadSession]);

  // The API client tells us when the session could not be renewed.
  useEffect(() => onSessionLost(clear), [clear]);

  const login = useCallback(
    async (email: string, password: string) => {
      const token = await api.post<TokenResponse>(
        '/auth/login',
        { email, password },
        { skipRefresh: true },
      );
      setAccessToken(token.access_token);
      await loadSession();
    },
    [loadSession],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', undefined, { skipRefresh: true });
    } catch (error) {
      // A failed logout must still clear the local state.
      if (!(error instanceof ApiError)) throw error;
    } finally {
      clear();
    }
  }, [clear]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      permissions,
      status,
      login,
      logout,
      hasRole: (...roles) => (user ? roles.includes(user.role) : false),
      can: (permission) => permissions.includes(permission),
      refreshUser: loadSession,
    }),
    [user, permissions, status, login, logout, loadSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// The hook lives with its context on purpose: splitting it into another file
// would only add an indirection. Fast refresh of this file is not a concern.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth doit être utilisé dans un <AuthProvider>.');
  return context;
}
